// 🔄 SISTEMA DE COLA OFFLINE PARA GASTOS
// Maneja el almacenamiento local y sincronización automática de gastos

import { Gasto } from './mockApi';

export interface OfflineExpense {
  idTemp: string;        // UUID temporal
  data: Omit<Gasto, 'id' | 'sincronizado'> & { 
    imagenBase64?: string; // Imagen en base64 para sincronización posterior
    imagenFileName?: string; // Nombre del archivo original
  };
  createdAt: string;      // Fecha del registro offline
  synced: boolean;        // Si ya fue sincronizado
  retryCount?: number;    // Contador de reintentos
}

const STORAGE_KEY = 'offline_expenses_queue';

/**
 * Convierte un archivo a base64
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Obtiene todos los gastos pendientes de sincronización
 */
export function getPendingExpenses(): OfflineExpense[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const expenses: OfflineExpense[] = JSON.parse(stored);
    // Filtrar solo los no sincronizados
    return expenses.filter(exp => !exp.synced);
  } catch (error) {
    console.error('❌ Error al leer gastos pendientes:', error);
    return [];
  }
}

/**
 * Agrega un gasto a la cola offline
 */
export async function addExpenseToQueue(
  gastoData: Omit<Gasto, 'id' | 'sincronizado'>,
  imagenFile?: File
): Promise<string> {
  try {
    // Generar ID temporal
    const idTemp = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Convertir imagen a base64 si existe
    let imagenBase64: string | undefined;
    let imagenFileName: string | undefined;
    
    if (imagenFile) {
      imagenBase64 = await fileToBase64(imagenFile);
      imagenFileName = imagenFile.name;
    }
    
    const offlineExpense: OfflineExpense = {
      idTemp,
      data: {
        ...gastoData,
        imagenBase64,
        imagenFileName
      },
      createdAt: new Date().toISOString(),
      synced: false,
      retryCount: 0
    };
    
    // Obtener gastos existentes
    const existing = getPendingExpenses();
    
    // Agregar el nuevo gasto
    const allExpenses = [...existing, offlineExpense];
    
    // Guardar en localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allExpenses));
    
    console.log('💾 Gasto guardado offline:', idTemp);
    
    return idTemp;
  } catch (error) {
    console.error('❌ Error al guardar gasto offline:', error);
    throw error;
  }
}

/**
 * Marca un gasto como sincronizado
 */
export function markExpenseAsSynced(idTemp: string): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    
    const expenses: OfflineExpense[] = JSON.parse(stored);
    const updated = expenses.map(exp => 
      exp.idTemp === idTemp ? { ...exp, synced: true } : exp
    );
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    console.log('✅ Gasto sincronizado:', idTemp);
  } catch (error) {
    console.error('❌ Error al marcar gasto como sincronizado:', error);
  }
}

/**
 * Elimina un gasto de la cola (después de sincronizar exitosamente)
 */
export function removeExpenseFromQueue(idTemp: string): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    
    const expenses: OfflineExpense[] = JSON.parse(stored);
    const filtered = expenses.filter(exp => exp.idTemp !== idTemp);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    console.log('🗑️ Gasto eliminado de la cola:', idTemp);
  } catch (error) {
    console.error('❌ Error al eliminar gasto de la cola:', error);
  }
}

/**
 * Incrementa el contador de reintentos de un gasto
 */
export function incrementRetryCount(idTemp: string): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    
    const expenses: OfflineExpense[] = JSON.parse(stored);
    const updated = expenses.map(exp => 
      exp.idTemp === idTemp 
        ? { ...exp, retryCount: (exp.retryCount || 0) + 1 }
        : exp
    );
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('❌ Error al incrementar contador de reintentos:', error);
  }
}

/**
 * Limpia todos los gastos sincronizados (mantiene solo los pendientes)
 */
export function cleanSyncedExpenses(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    
    const expenses: OfflineExpense[] = JSON.parse(stored);
    const pending = expenses.filter(exp => !exp.synced);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch (error) {
    console.error('❌ Error al limpiar gastos sincronizados:', error);
  }
}

/**
 * Obtiene el conteo de gastos pendientes
 */
export function getPendingCount(): number {
  return getPendingExpenses().length;
}

